// Pick the largest uncovered edge of a retained image in the current view.
// Bounds and the returned anchor use the caller's normalized screen axes.
export function panEdgeAnchor(bounds) {
  if (!bounds || bounds.length !== 4 || !bounds.every(Number.isFinite)) return null;
  if (bounds[2] <= bounds[0] || bounds[3] <= bounds[1]) return null;
  const [left, top, right, bottom] = bounds.map(value => Math.max(0, Math.min(1, value)));
  if (right <= left || bottom <= top) return [0.5, 0.5];
  const strips = [[0, 0, left, 1], [right, 0, 1, 1],
    [left, 0, right, top], [left, bottom, right, 1]];
  let largest = null, area = 0;
  for (const strip of strips) {
    const size = (strip[2] - strip[0]) * (strip[3] - strip[1]);
    if (size > area) { largest = strip; area = size; }
  }
  return largest ? [(largest[0] + largest[2]) / 2, (largest[1] + largest[3]) / 2] : null;
}

// Copy retained pixels by row. The destination starts transparent, so uncovered
// edges can be filled independently by the renderer.
export function copyPanPixels(source, destination, width, height, offsetX, offsetY) {
  const srcX = Math.max(0, offsetX);
  const dstX = Math.max(0, -offsetX);
  const copyWidth = width - Math.abs(offsetX);
  if (copyWidth <= 0) return;
  const endY = Math.min(height, height + offsetY);
  for (let srcY = Math.max(0, offsetY), dstY = Math.max(0, -offsetY); srcY < endY; srcY++, dstY++) {
    const start = (srcY * width + srcX) * 4;
    destination.set(source.subarray(start, start + copyWidth * 4), (dstY * width + dstX) * 4);
  }
}

// Present completed worker tiles without reading the source canvas back to JS.
// Ordinary pans move retained pixels in the canvas backend and upload only the
// newly exposed tiles. A no-retention plan assembles one full upload.
export function presentPanTiles(canvas, context, result) {
  const { pixelOffsetX: dx, pixelOffsetY: dy, regions } = result;
  if (dx === 0 && dy === 0 && regions.length) {
    const image = context.createImageData(canvas.width, canvas.height);
    for (const region of regions) {
      const rowBytes = region.width * 4;
      for (let row = 0; row < region.height; row++) {
        image.data.set(region.pixels.subarray(row * rowBytes, (row + 1) * rowBytes),
          ((region.y + row) * canvas.width + region.x) * 4);
      }
    }
    context.putImageData(image, 0, 0);
    return;
  }
  if (dx !== 0 || dy !== 0) {
    const sourceX = Math.max(0, dx);
    const sourceY = Math.max(0, dy);
    const destinationX = Math.max(0, -dx);
    const destinationY = Math.max(0, -dy);
    const retainedWidth = canvas.width - Math.abs(dx);
    const retainedHeight = canvas.height - Math.abs(dy);
    context.save();
    try {
      // Source-over would leave stale destination colors underneath the
      // transparent interior of the fractal. Copy replaces those pixels too.
      context.globalCompositeOperation = 'copy';
      context.drawImage(canvas, sourceX, sourceY, retainedWidth, retainedHeight,
        destinationX, destinationY, retainedWidth, retainedHeight);
    } finally {
      context.restore();
    }
  }
  for (const region of regions) {
    context.putImageData(new ImageData(region.pixels, region.width, region.height),
      region.x, region.y);
  }
}

export function planPan({ canvasWidth: w, canvasHeight: h, oldXRange, oldYRange, newXRange, newYRange }) {
  const spanX = newXRange[1] - newXRange[0];
  const spanY = newYRange[1] - newYRange[0];
  const dx = Math.round((newXRange[0] - oldXRange[0]) / (spanX / w));
  const dy = Math.round((newYRange[0] - oldYRange[0]) / (spanY / h));
  if (!Number.isFinite(dx) || !Number.isFinite(dy) || Math.abs(dx) >= w || Math.abs(dy) >= h ||
      Math.abs(spanX - (oldXRange[1] - oldXRange[0])) > 1e-10 ||
      Math.abs(spanY - (oldYRange[1] - oldYRange[0])) > 1e-10) {
    return { pixelOffsetX: 0, pixelOffsetY: 0, regions: [{ x: 0, y: 0, width: w, height: h }] };
  }
  const regions = [];
  if (dx !== 0) regions.push({ x: dx > 0 ? w - dx : 0, y: 0, width: Math.abs(dx), height: h });
  if (dy !== 0) regions.push({ x: Math.max(0, -dx), y: dy > 0 ? h - dy : 0, width: w - Math.abs(dx), height: Math.abs(dy) });
  return { pixelOffsetX: dx, pixelOffsetY: dy, regions };
}
